import { useRef, useState, useEffect } from 'react';

/**
 * 포인터 이벤트 기반 wrap-flex 레이아웃 순서 변경 훅
 *
 * 마우스/터치/펜 통합 처리. dnd-kit 없이 직접 구현.
 * 가변 폭 아이템 + flex-wrap 줄바꿈 + 갭 기반 스냅 + 슬라이드용 framer-motion 시각효과
 * 조합을 원할 때 사용. 시각 요소(TempButton, 애니메이션, wrapper 구조)는 호출자가 그림.
 *
 * 사용법:
 *   const { containerRef, dragSourceIndex, dragOverIndex, dragOverSide, draggedWidth, onPointerDown }
 *     = usePointerReorder({ onDrop: (sourceIdx, targetIdx, side) => ... });
 *
 *   <Container ref={containerRef}>
 *     {items.map((item, i) => (
 *       <Item key={i} data-reorder-index={i}>
 *         <Handle onPointerDown={(e) => onPointerDown(e, i)} />
 *       </Item>
 *     ))}
 *   </Container>
 *
 * @param {object} options
 * @param {(sourceIdx, targetIdx, side) => void} options.onDrop  드롭 시 호출 (side: 'left'|'right'|'self')
 * @param {number} [options.touchHoldDuration=500]  터치 hold 시간(ms). 브라우저 long-press(550ms)와 같음
 * @param {number} [options.activationDistance=5]   마우스/펜 활성화 거리(px), 터치 스크롤 판정 임계값
 */
export default function usePointerReorder(options = {}) {
  const {
    onDrop,
    touchHoldDuration = 250,
    activationDistance = 5,
  } = options;

  const [dragSourceIndex, setDragSourceIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [draggedWidth, setDraggedWidth] = useState(0);

  const containerRef = useRef(null);
  const cachedItemRects = useRef(null);
  const pointerDragRef = useRef(null);

  // 안정적인 핸들러 프록시 (한 번만 생성, 매 렌더 실제 로직 갱신) //
  const pointerHandlersRef = useRef({ move: () => {}, up: () => {}, cancel: () => {} });
  const pointerProxyMove = useRef((e) => pointerHandlersRef.current.move(e)).current;
  const pointerProxyUp = useRef((e) => pointerHandlersRef.current.up(e)).current;
  const pointerProxyCancel = useRef((e) => pointerHandlersRef.current.cancel(e)).current;

  function preventDefaultHandler(e) {
    e.preventDefault();
  }

  // 드래그 시작 시 아이템 rect 캐싱 //
  function cacheItemRects() {
    const container = containerRef.current;
    if (!container) return;
    const items = container.querySelectorAll('[data-reorder-index]');
    cachedItemRects.current = Array.from(items).map(el => ({
      rect: el.getBoundingClientRect(),
      index: Number(el.dataset.reorderIndex),
    }));
  }

  // 갭 기반 스냅: 행 양 끝 + 버튼 사이 갭 중앙을 스냅 포인트로, 가장 가까운 곳 선택 //
  function findNearestTarget(clientX, clientY, sourceIdx) {
    const infos = cachedItemRects.current;
    if (!infos || infos.length === 0) return null;

    // 행 그룹핑 (top 좌표 기준) //
    const rowMap = new Map();
    for (const info of infos) {
      const key = Math.round(info.rect.top);
      if (!rowMap.has(key)) rowMap.set(key, []);
      rowMap.get(key).push(info);
    }

    // Y 거리 기반으로 가장 가까운 행 찾기 //
    let nearestRow = null;
    let nearestRowDist = Infinity;
    for (const row of rowMap.values()) {
      const top = row[0].rect.top;
      const bottom = top + row[0].rect.height;
      const dist = clientY < top ? top - clientY : clientY > bottom ? clientY - bottom : 0;
      if (dist < nearestRowDist) {
        nearestRowDist = dist;
        nearestRow = row;
      }
    }
    if (!nearestRow) return null;

    const sorted = nearestRow.slice().sort((a, b) => a.rect.left - b.rect.left);

    // 스냅 포인트: 행 왼쪽 끝, 버튼 사이 갭 중앙, 행 오른쪽 끝 //
    const snaps = [];
    snaps.push({ x: sorted[0].rect.left, index: sorted[0].index, side: 'left' });
    for (let i = 0; i < sorted.length - 1; i++) {
      const gapCenter = (sorted[i].rect.right + sorted[i + 1].rect.left) / 2;
      snaps.push({ x: gapCenter, index: sorted[i].index, side: 'right' });
    }
    const last = sorted[sorted.length - 1];
    snaps.push({ x: last.rect.right, index: last.index, side: 'right' });

    // 커서 X와 가장 가까운 스냅 선택 //
    let nearest = snaps[0];
    let nearestDist = Math.abs(clientX - nearest.x);
    for (let i = 1; i < snaps.length; i++) {
      const dist = Math.abs(clientX - snaps[i].x);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = snaps[i];
      }
    }

    // 스냅이 소스의 원래 영역(X+Y) 안이면 self //
    const sourceRect = infos.find(b => b.index === sourceIdx)?.rect;
    if (sourceRect &&
        nearest.x >= sourceRect.left - 5 && nearest.x <= sourceRect.right + 5 &&
        clientY >= sourceRect.top && clientY <= sourceRect.bottom) {
      return { index: sourceIdx, side: 'self' };
    }

    return { index: nearest.index, side: nearest.side };
  }

  // 매 렌더마다 최신 핸들러 갱신 (closure 로 최신 onDrop / option 캡처) //
  pointerHandlersRef.current.move = function handlePointerMove(e) {
    const state = pointerDragRef.current;
    if (!state || state.pointerId !== e.pointerId) return;

    const dx = e.clientX - state.startX;
    const dy = e.clientY - state.startY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (!state.hasActivated) {
      if (state.pointerType === 'mouse' || state.pointerType === 'pen') {
        if (dist > activationDistance) activatePointerDrag(e.clientX, e.clientY);
      }
      else if (state.pointerType === 'touch') {
        // hold timer 동작 중 임계값 이상 움직이면 스크롤로 간주, 취소 //
        if (dist > activationDistance && state.holdTimer != null) {
          window.clearTimeout(state.holdTimer);
          cleanupPointerDrag();
          return;
        }
      }
      if (!state.hasActivated) return;
    }

    // 고스트 위치 갱신 //
    if (state.ghostElement) {
      state.ghostElement.style.left = `${e.clientX - state.offsetX}px`;
      state.ghostElement.style.top = `${e.clientY - state.offsetY}px`;
    }

    // 스냅 감지 //
    const target = findNearestTarget(e.clientX, e.clientY, state.targetIndex);
    if (target) {
      state.currentOverIndex = target.index;
      state.currentOverSide = target.side;
      setDragOverIndex(prev => prev !== target.index ? target.index : prev);
      setDragOverSide(prev => prev !== target.side ? target.side : prev);
    }

    // 터치 스크롤 방지 //
    if (state.pointerType === 'touch') e.preventDefault();
  };

  pointerHandlersRef.current.up = function handlePointerUp(e) {
    const state = pointerDragRef.current;
    if (!state || state.pointerId !== e.pointerId) return;

    if (state.hasActivated && state.targetIndex !== null && state.currentOverIndex !== null) {
      if (onDrop) onDrop(state.targetIndex, state.currentOverIndex, state.currentOverSide);
    }
    cleanupPointerDrag();
  };

  pointerHandlersRef.current.cancel = function handlePointerCancel(e) {
    const state = pointerDragRef.current;
    if (!state || state.pointerId !== e.pointerId) return;
    cleanupPointerDrag();
  };

  function onPointerDown(e, index) {
    if (pointerDragRef.current) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    // e.currentTarget 은 드래그 핸들. 부모 [data-reorder-index] 요소를 ghost/rect 기준으로 사용 //
    const handle = e.currentTarget;
    const itemEl = handle.closest('[data-reorder-index]');
    if (!itemEl) return;
    const rect = itemEl.getBoundingClientRect();

    // 포인터 캡처: 브라우저 gesture engine 에서 이 포인터를 우리한테 전용 할당 //
    // long-press, 스와이프 감지 등 시스템 제스처 차단 //
    try { handle.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }

    pointerDragRef.current = {
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      targetIndex: index,
      targetElement: itemEl,
      targetWidth: Math.ceil(rect.width),
      targetHeight: Math.ceil(rect.height),
      ghostElement: null,
      hasActivated: false,
      holdTimer: null,
      currentOverIndex: null,
      currentOverSide: null,
      captureElement: handle,
    };

    // 터치: 일정 시간 누르고 있어야 드래그 시작 (스크롤과 구분) //
    if (e.pointerType === 'touch') {
      pointerDragRef.current.holdTimer = window.setTimeout(() => {
        const s = pointerDragRef.current;
        if (s) activatePointerDrag(s.startX, s.startY);
      }, touchHoldDuration);
    }

    window.addEventListener('pointermove', pointerProxyMove, { passive: false });
    window.addEventListener('pointerup', pointerProxyUp);
    window.addEventListener('pointercancel', pointerProxyCancel);
    // Android Chrome long-press 가 만들어내는 contextmenu/selection 을 window 레벨에서 차단 //
    window.addEventListener('contextmenu', preventDefaultHandler);
    window.addEventListener('selectstart', preventDefaultHandler);

    // iOS Safari: drag 도중 텍스트 선택/복사 콜아웃이 element 단위 user-select 만으로 안 막혀서 body 에 강제 적용 //
    pointerDragRef.current.prevBodyUserSelect = document.body.style.webkitUserSelect;
    pointerDragRef.current.prevBodyTouchCallout = document.body.style.webkitTouchCallout;
    document.body.style.webkitUserSelect = 'none';
    document.body.style.userSelect = 'none';
    document.body.style.webkitTouchCallout = 'none';
  }

  function activatePointerDrag(clientX, clientY) {
    const state = pointerDragRef.current;
    if (!state || state.hasActivated) return;
    state.hasActivated = true;

    if (state.holdTimer != null) {
      window.clearTimeout(state.holdTimer);
      state.holdTimer = null;
    }

    // 고스트 생성 (opacity 0.5, 커서 따라옴, 두 줄로 늘어나지 않도록 nowrap+height 고정) //
    const ghost = state.targetElement.cloneNode(true);
    ghost.style.position = 'fixed';
    ghost.style.left = `${clientX - state.offsetX}px`;
    ghost.style.top = `${clientY - state.offsetY}px`;
    ghost.style.width = `${state.targetWidth}px`;
    ghost.style.maxWidth = '90vw';
    ghost.style.height = `${state.targetHeight}px`;
    ghost.style.opacity = '0.5';
    ghost.style.pointerEvents = 'none';
    ghost.style.zIndex = '9999';
    ghost.style.margin = '0';
    ghost.style.whiteSpace = 'nowrap';
    ghost.style.overflow = 'hidden';
    document.body.appendChild(ghost);
    state.ghostElement = ghost;

    setDraggedWidth(state.targetWidth);
    cacheItemRects();
    setDragSourceIndex(state.targetIndex);
    setDragOverIndex(state.targetIndex);
    setDragOverSide('self');
    state.currentOverIndex = state.targetIndex;
    state.currentOverSide = 'self';
  }

  function cleanupPointerDrag() {
    const state = pointerDragRef.current;
    if (!state) return;

    if (state.captureElement) {
      try { state.captureElement.releasePointerCapture(state.pointerId); } catch (err) { /* ignore */ }
    }

    if (state.ghostElement && state.ghostElement.parentNode) {
      state.ghostElement.parentNode.removeChild(state.ghostElement);
    }
    if (state.holdTimer != null) {
      window.clearTimeout(state.holdTimer);
    }

    // iOS Safari: drag 시작 시 강제 적용했던 body user-select 복원 //
    document.body.style.webkitUserSelect = state.prevBodyUserSelect ?? '';
    document.body.style.userSelect = '';
    document.body.style.webkitTouchCallout = state.prevBodyTouchCallout ?? '';

    pointerDragRef.current = null;

    setDragSourceIndex(null);
    setDragOverIndex(null);
    setDragOverSide(null);
    setDraggedWidth(0);
    cachedItemRects.current = null;

    window.removeEventListener('pointermove', pointerProxyMove);
    window.removeEventListener('pointerup', pointerProxyUp);
    window.removeEventListener('pointercancel', pointerProxyCancel);
    window.removeEventListener('contextmenu', preventDefaultHandler);
    window.removeEventListener('selectstart', preventDefaultHandler);
  }

  // 컴포넌트 언마운트 시 안전 정리 //
  useEffect(() => {
    return () => cleanupPointerDrag();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    containerRef,
    dragSourceIndex,
    dragOverIndex,
    dragOverSide,
    draggedWidth,
    onPointerDown,
  };
}
